import type { ReactElement, ReactNode } from 'react';

/**
 * The skeleton every public Fias page shares — fias.io and the Games page: a
 * skip link to the main content, then the header, main and footer landmarks.
 * Each page fills the slots with its own markup, classes and words, so the
 * shell decides structure and accessibility, never the look.
 *
 * Plain markup with no context and no hooks, so it renders the same in the
 * app, in fias.io's prerender and in the Games snapshot.
 */
export function PublicSiteShell({
  className,
  mainId,
  skipLinkClassName,
  headerClassName,
  header,
  footerClassName,
  footer,
  after,
  children,
}: {
  /** The page root's class (the page's own design scope). */
  className: string;
  /** The main landmark's id — the skip link's target. Unique per page. */
  mainId: string;
  skipLinkClassName: string;
  headerClassName: string;
  /** What the header holds. */
  header: ReactNode;
  footerClassName: string;
  /** What the footer holds. */
  footer: ReactNode;
  /** Anything after the footer inside the page root, e.g. a closed dialog. */
  after?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={className}>
      <a href={`#${mainId}`} className={skipLinkClassName}>
        Skip to content
      </a>
      <header className={headerClassName}>{header}</header>
      <main id={mainId}>{children}</main>
      <footer className={footerClassName}>{footer}</footer>
      {after}
    </div>
  );
}
